import "react-image-crop/dist/ReactCrop.css";

import React, { useCallback, useState, useRef } from "react";
import ReactCrop, {
	centerCrop,
	makeAspectCrop,
	PixelCrop,
} from "react-image-crop";

import Button from "../../../common/Button/Button";
import LoadingSpinner from "../../../common/LoadingSpinner/LoadingSpinner";

import {
	canvasPreview,
	convertCanvasToBlob,
} from "../../../../lib/util/ImageUtils";
import { useDebouncedEffect } from "../../../../lib/hook/MiscHooks";
import { SignedImage } from "../../../../lib/types/MiscTypes";
import { useImmutable } from "../../../../lib/hook/SwrHooks";

import * as Api from "../../../../Api";
import CompetitionCard from "../../../../views/RewardsBuilder/sub-components/CompetitionCard";

interface EditImageProps {
	src: SignedImage;
	onCancel: () => void;
	onSave: (file: File) => void;
	saving: boolean;
}

const EditImage: React.FC<EditImageProps> = ({
	src,
	onCancel,
	onSave,
	saving,
}) => {
	const { data: editableImage } = useImmutable(
		src.name,
		async () => {
			const res = await Api.postGetEditableRewardsImage.submit({
				body: { imagePath: src.name },
			});
			return res?.data;
		},
		null,
	);

	const [crop, setCrop] = useState<any>();
	const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
	const [previewCrop, setPreviewCrop] = useState<any>();
	const [scale, setScale] = useState(1);
	const [previewCropSrc, setPreviewCropSrc] = useState<string | null>(null);
	const previewCanvasRef = useRef<HTMLCanvasElement>(null);
	const imgRef = useRef<HTMLImageElement>(null);

	const togglePreviewCrop = useCallback(() => {
		setPreviewCrop(!previewCrop);
	}, [previewCrop]);

	const onCropComplete = useCallback((c) => {
		setCompletedCrop(c);
	}, []);

	const handleImageLoad = useCallback((evt) => {
		const { naturalWidth, naturalHeight, width, height } = evt.currentTarget;
		let cropW = 686;
		let cropH = 304;

		if (naturalWidth < cropW || naturalHeight < cropH) {
			cropW = 343;
			cropH = 152;
		}

		const percW = cropW / naturalWidth;
		const percH = cropH / naturalHeight;

		const crop = centerCrop(
			makeAspectCrop(
				{
					unit: "%",
					width: percW * 100,
					height: percH * 100,
				},
				cropW / cropH,
				width,
				height,
			),
			width,
			height,
		);

		setCrop(crop);
	}, []);

	const handleChange = useCallback((_, percentCrop) => {
		setCrop(percentCrop);
	}, []);

	useDebouncedEffect(
		async () => {
			if (
				completedCrop?.width &&
				completedCrop?.height &&
				imgRef.current &&
				previewCanvasRef.current
			) {
				const dataURL = await canvasPreview(
					imgRef.current,
					previewCanvasRef.current,
					completedCrop,
					scale,
				);

				if (!dataURL) {
					return;
				}

				setPreviewCropSrc(dataURL);
			}
		},
		[completedCrop, scale],
		100,
	);

	const handleScaleChange = useCallback(
		(evt) => {
			const operation = evt.currentTarget.dataset.op;
			setScale(operation === "increase" ? scale + 0.1 : scale - 0.1);
		},
		[scale],
	);

	const handleSave = useCallback(async () => {
		if (!imgRef.current || !previewCanvasRef.current || !completedCrop) {
			return;
		}

		const blob = await convertCanvasToBlob(
			imgRef.current,
			previewCanvasRef.current,
			completedCrop,
		);
		const fileName = src.name.substring(src.name.lastIndexOf("/") + 1);
		const file = new File([blob], fileName, { type: "image/png" });
		await onSave(file);
	}, [completedCrop, onSave, src.name]);

	return !editableImage ? (
		<div className={"EditImage  --loading"}>
			<LoadingSpinner />
		</div>
	) : (
		<div className={"EditImage"}>
			<h3>Edit Image</h3>

			<div
				className={"EditImage__preview"}
				style={{ display: previewCrop ? "none" : "flex" }}
			>
				<div className={"EditImage__crop-wrapper"}>
					<ReactCrop
						onChange={handleChange}
						onComplete={onCropComplete}
						crop={crop}
						locked={true}
						maxWidth={500}
						maxHeight={500}
					>
						<img
							ref={imgRef}
							src={`data:${editableImage.fileType};base64, ${editableImage.encodedImage}`}
							alt={src.name}
							onLoad={handleImageLoad}
							style={{ transform: `scale(${scale})` }}
						/>
					</ReactCrop>
				</div>

				<div className={"EditImage__controls"}>
					<Button onClick={handleScaleChange} data-op={"increase"}>
						+
					</Button>
					<Button onClick={handleScaleChange} data-op={"decrease"}>
						-
					</Button>
				</div>
			</div>

			{previewCrop && (
				<div className={"EditImage__competition-card"}>
					<CompetitionCard
						competition={{}}
						isEditable={true}
						img={
							previewCropSrc && (
								<img src={previewCropSrc} style={{ width: "100%" }} alt={""} />
							)
						}
					/>
				</div>
			)}

			<canvas
				ref={previewCanvasRef}
				style={{
					objectFit: "contain",
					width: completedCrop?.width,
					height: completedCrop?.height,
					display: "none",
				}}
			/>

			<div className={"EditImage__actions"}>
				{!previewCrop ? (
					<>
						<Button onClick={onCancel} outlined={true}>
							Back
						</Button>
						<Button onClick={togglePreviewCrop}>Crop</Button>
					</>
				) : (
					<>
						<Button onClick={togglePreviewCrop} outlined={true}>
							Undo
						</Button>
						<Button onClick={handleSave} loading={saving}>
							Save
						</Button>
					</>
				)}
			</div>
		</div>
	);
};

export default EditImage;
